import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { IconHelp, IconPlus } from '../../icons';
import { DateTime } from 'luxon';
import useGuideStore from '../../store/guide';
import { useWorkOrderInstructions } from '../../hooks/useWorkOrderInstructions';
import useWOInstructionManagementDialog from '../../store/woInstructionManagementDialog';
import { useWOInstructionMutations } from '../../hooks/useWorkOrderInstructions';
import Alert from '../core/Alert';
import Button from '../core/Button';
import IconButton from '../core/IconButton';
import Input from '../core/Input';
import { DialogWindow } from '../DialogWindow';
import { cn } from '../../helpers/classnames';
import TagSelector from '../Tags/TagSelector';
import UnsavedChangesDialog from '../UnsavedChangesDialog';

interface TemporaryInstruction
  extends Omit<WorkOrderInstruction, 'id' | 'texts'> {
  id?: string;
  description: string;
}

const messages = defineMessages({
  newTitle: {
    id: 'template_manager.enter_template_title',
    defaultMessage: 'Enter template title',
  },
  workInstructions: {
    id: 'template_manager.work_instructions',
    defaultMessage: 'Enter work instructions here...',
  },
  saveFailed: {
    id: 'template_manager.save_failed',
    defaultMessage: 'Save failed. Please try again.',
  },
  createFailed: {
    id: 'template_manager.create_failed',
    defaultMessage: 'Creating new template failed. Please try again.',
  },
  deleteFailed: {
    id: 'template_manager.delete_failed',
    defaultMessage: 'Deleting template failed. Please try again.',
  },
  templateManager: {
    id: 'template_manager.template_manager',
    defaultMessage: 'Template Manager',
  },
  templateTitle: {
    id: 'template_manager.template_title',
    defaultMessage: 'Template title',
  },
  workInstructionsLabel: {
    id: 'template_manager.work_instructions_label',
    defaultMessage: 'Work instructions',
  },
  newTemplate: {
    id: 'template_manager.new_template',
    defaultMessage: 'New template',
  },
  error: {
    id: 'template_manager.error',
    defaultMessage: 'Error',
  },
  help: {
    id: 'help',
    defaultMessage: 'Help',
  },
});

const WorkOrderInstructionManager: React.FC = () => {
  const intl = useIntl();
  const { openGuide } = useGuideStore();

  const { list: instructions } = useWorkOrderInstructions();
  const {
    create: {
      mutateAsync: createInstruction,
      isLoading: createInstructionInProgress,
    },
    update: {
      mutateAsync: updateInstruction,
      isLoading: updateInstructionInProgress,
    },
    remove: {
      mutateAsync: deleteInstruction,
      isLoading: deleteInstructionInProgress,
    },
  } = useWOInstructionMutations();
  const { show, close, selectedInstructionId, setSelectedInstructionId } =
    useWOInstructionManagementDialog();

  const [confirmDirty, setConfirmDirty] = useState(false);
  const [changes, setChanges] = useState(false);

  const [localInstruction, setLocalInstruction] =
    useState<TemporaryInstruction | null>({
      description: '',
      title: '',
      tags: [],
      meta: {
        created_at: DateTime.now(),
        created_by: '',
        updated_at: DateTime.now(),
      },
    });
  const titleRef = useRef<HTMLInputElement | null>(null);

  const [updateFailed, setUpdateFailed] = useState('');

  const setLocalInstructionAndFlagChanges = useCallback(
    (instruction: TemporaryInstruction) => {
      setChanges(true);
      setLocalInstruction(instruction);
    },
    [setLocalInstruction],
  );

  const reset = useCallback(() => {
    setChanges(false);
    setConfirmDirty(false);
    setUpdateFailed('');
    setLocalInstruction({
      description: '',
      title: '',
      tags: [],
      meta: {
        created_at: DateTime.now(),
        created_by: '',
        updated_at: DateTime.now(),
      },
    });
    setSelectedInstructionId(null);
  }, [setSelectedInstructionId]);

  useEffect(() => {
    if (
      (!localInstruction || localInstruction.id !== selectedInstructionId) &&
      selectedInstructionId
    ) {
      const instruction = instructions.find(
        (t) => t.id === selectedInstructionId,
      );
      if (instruction) {
        setLocalInstruction({
          id: selectedInstructionId,
          description: instruction.texts.join('\n'),
          tags: instruction.tags,
          title: instruction.title,
          meta: instruction.meta,
        });
      }
    }
  }, [localInstruction, selectedInstructionId, instructions]);

  const saveEditedTemplate = useCallback(() => {
    if (localInstruction) {
      const id = localInstruction.id;
      const description = localInstruction.description.split(/\r?\n/);
      if (id) {
        void updateInstruction({
          id,
          ...localInstruction,
          texts: description,
        })
          .catch(() => {
            setUpdateFailed(intl.formatMessage(messages.saveFailed));
          })
          .then(reset);
      } else {
        void createInstruction({ ...localInstruction, texts: description })
          .catch(() => {
            setUpdateFailed(intl.formatMessage(messages.createFailed));
          })
          .then(reset);
      }
    }
  }, [createInstruction, intl, localInstruction, updateInstruction, reset]);

  const deleteAndClear = useCallback(
    (templateId: string) => {
      void deleteInstruction(templateId)
        .catch(() => {
          setUpdateFailed(intl.formatMessage(messages.deleteFailed));
        })
        .then(reset);
    },
    [deleteInstruction, intl, reset],
  );

  const clear = useCallback(() => {
    reset();
    if (titleRef.current) {
      titleRef.current.focus();
    }
  }, [reset]);

  const processing = useMemo(() => {
    return (
      createInstructionInProgress ||
      updateInstructionInProgress ||
      deleteInstructionInProgress
    );
  }, [
    createInstructionInProgress,
    deleteInstructionInProgress,
    updateInstructionInProgress,
  ]);

  const tryClose = useCallback(() => {
    if (changes) {
      setConfirmDirty(true);
    } else {
      close();
    }
  }, [changes, close]);

  const content = (
    <div className="flex flex-row">
      <div className="mt-1 flex-1">
        <div className="flex items-center justify-between">
          <h5 className="text-xl font-semibold text-text-950">
            <FormattedMessage
              id="template_manager.instructions"
              defaultMessage="Instructions"
            />
          </h5>
          <IconButton
            variant="ghost"
            round
            icon={<IconPlus />}
            aria-label={intl.formatMessage(messages.newTemplate)}
            onClick={clear}
          />
        </div>
        <ul className="py-2">
          {instructions.map((t) => (
            <li key={t.id}>
              <button
                type="button"
                aria-current={
                  localInstruction?.id === t.id ? 'true' : undefined
                }
                onClick={() => setSelectedInstructionId(t.id)}
                className={cn(
                  'flex w-full px-4 py-2 text-left text-base leading-6 hover:bg-black/5',
                  localInstruction?.id === t.id && 'bg-black/[0.08]',
                )}
              >
                {t.title}
              </button>
            </li>
          ))}
        </ul>
      </div>
      {localInstruction && (
        <>
          <div className="flex flex-[2] flex-col bg-background-50 p-4">
            <div className="pt-6">
              <Input
                id="template-manager-title"
                ref={titleRef}
                label={intl.formatMessage(messages.templateTitle)}
                placeholder={intl.formatMessage(messages.newTitle)}
                value={localInstruction.title}
                onChange={(event) =>
                  setLocalInstructionAndFlagChanges({
                    ...localInstruction,
                    title: event.target.value,
                  })
                }
              />
            </div>
            <div className="pt-8">
              <Input
                id="template-manager-instructions"
                type="multiline"
                rows={14}
                label={intl.formatMessage(messages.workInstructionsLabel)}
                placeholder={intl.formatMessage(messages.workInstructions)}
                value={localInstruction.description}
                onChange={(event) =>
                  setLocalInstructionAndFlagChanges({
                    ...localInstruction,
                    description: event.target.value,
                  })
                }
              />
            </div>
            <div className="mt-2 flex justify-between">
              <Button
                variant="outline"
                onClick={() =>
                  setLocalInstructionAndFlagChanges({
                    ...localInstruction,
                    description: '',
                  })
                }
              >
                <FormattedMessage
                  id="template_manager.clear"
                  defaultMessage="Clear"
                />
              </Button>
            </div>
          </div>
          <div className="flex flex-1 flex-col bg-background-50">
            <div className="mb-2 mt-2 flex items-center">
              <p className="text-[10px] uppercase leading-4 tracking-[1.5px] text-text-800">
                <FormattedMessage
                  id="template_manager.tags_count_selected"
                  defaultMessage="Tags ({count} selected)"
                  values={{ count: localInstruction?.tags?.length || 0 }}
                />
              </p>
            </div>
            <div className="flex flex-wrap">
              <TagSelector
                selectedTagIds={localInstruction?.tags || []}
                onChange={(tagIds) => {
                  if (localInstruction) {
                    setLocalInstructionAndFlagChanges({
                      ...localInstruction,
                      tags: tagIds,
                    });
                  }
                }}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );

  const footer = (
    <>
      {updateFailed && (
        <Alert
          severity="error"
          title={intl.formatMessage(messages.error)}
          className="mr-auto"
        >
          {updateFailed}
        </Alert>
      )}
      <Button
        variant="outline"
        disabled={!localInstruction || !localInstruction.id || processing}
        onClick={() =>
          localInstruction &&
          localInstruction.id &&
          deleteAndClear(localInstruction.id)
        }
      >
        <FormattedMessage
          id="template_manager.remove_template"
          defaultMessage="Remove template"
        />
      </Button>
      <Button variant="outline" onClick={() => tryClose()}>
        <FormattedMessage
          id="template_manager.cancel"
          defaultMessage="Cancel"
        />
      </Button>
      <Button
        variant="filled"
        onClick={() => saveEditedTemplate()}
        disabled={!localInstruction || !localInstruction.title || processing}
      >
        {localInstruction && localInstruction.id ? (
          <FormattedMessage
            id="template_manager.save_template"
            defaultMessage="Save template"
          />
        ) : (
          <FormattedMessage
            id="template_manager.create_template"
            defaultMessage="Create template"
          />
        )}
      </Button>
    </>
  );

  return (
    <>
      <UnsavedChangesDialog
        open={!!confirmDirty}
        onOk={() => {
          setChanges(false);
          setConfirmDirty(false);
          close();
          reset();
        }}
        onCancel={() => setConfirmDirty(false)}
      />

      <DialogWindow
        open={show}
        onOpenChange={(open) => !open && tryClose()}
        title={intl.formatMessage(messages.templateManager)}
        headerActions={
          <IconButton
            variant="ghost"
            icon={<IconHelp />}
            aria-label={intl.formatMessage(messages.help)}
            onClick={() => openGuide('WorkOrderTemplateManager')}
          />
        }
        content={content}
        footer={footer}
      />
    </>
  );
};

export default WorkOrderInstructionManager;
